import type {
    AddToCartResult,
    LiveRevalidation,
    ShopSearchChip,
    ShopSearchFilters,
    ShopSearchResponse,
    ShopSearchCatalogInitData,
} from './types';

function baseUrl(init: ShopSearchCatalogInitData): string {
    return String(init.rest_base_url || '/wp-json/gas/v1').replace(/\/$/, '');
}

function headers(init: ShopSearchCatalogInitData): HeadersInit {
    return {
        'Content-Type': 'application/json',
        'X-WP-Nonce': init.rest_nonce || '',
    };
}

async function postJson<T>(
    init: ShopSearchCatalogInitData,
    path: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
): Promise<T> {
    const res = await fetch(`${baseUrl(init)}${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(init),
        body: JSON.stringify(body),
        signal,
    });

    const json = (await res.json().catch(() => ({}))) as {
        data?: T | Record<string, unknown>;
        code?: string;
        message?: string;
    };

    if (!res.ok) {
        const err = new Error(json?.message || 'shop_search_error') as Error & {
            code?: string;
            status?: number;
            payload?: Record<string, unknown>;
        };
        err.code = json?.code;
        err.status = res.status;
        err.payload = (typeof json.data === 'object' && json.data !== null
            ? (json.data as Record<string, unknown>)
            : {}) as Record<string, unknown>;
        throw err;
    }

    // Enveloppe REST { data, meta }.
    return (json.data ?? json) as T;
}

export function searchNatural(
    init: ShopSearchCatalogInitData,
    query: string,
    signal?: AbortSignal,
): Promise<ShopSearchResponse> {
    return postJson<ShopSearchResponse>(
        init,
        '/shop/search',
        {
            mode: 'resolve',
            query,
            locale: init.locale || 'fr',
        },
        signal,
    );
}

export function searchApply(
    init: ShopSearchCatalogInitData,
    filters: ShopSearchFilters,
    chips: ShopSearchChip[],
    options: { residual?: string; signal?: AbortSignal } = {},
): Promise<ShopSearchResponse> {
    return postJson<ShopSearchResponse>(
        init,
        '/shop/search',
        {
            mode: 'apply',
            filters,
            chips,
            residual: options.residual || '',
            locale: init.locale || 'fr',
        },
        options.signal,
    );
}

export function revalidateVariant(
    init: ShopSearchCatalogInitData,
    shopVariantId: number,
    quantity = 1,
    signal?: AbortSignal,
): Promise<LiveRevalidation> {
    return postJson<LiveRevalidation>(
        init,
        '/shop/search/revalidate',
        {
            shop_variant_id: shopVariantId,
            quantity,
        },
        signal,
    );
}

export function addToCartFromSearch(
    init: ShopSearchCatalogInitData,
    payload: {
        shop_variant_id: number;
        query_id: string;
        shop_product_id?: number;
        quantity?: number;
    },
    signal?: AbortSignal,
): Promise<AddToCartResult> {
    return postJson<AddToCartResult>(
        init,
        '/shop/search/add-to-cart',
        {
            shop_variant_id: payload.shop_variant_id,
            query_id: payload.query_id,
            shop_product_id: payload.shop_product_id,
            quantity: payload.quantity ?? 1,
        },
        signal,
    );
}

/** Télémétrie insights — soft-fail si consentement refusé (200 accepted:false). */
export async function trackSearchRun(
    init: ShopSearchCatalogInitData,
    payload: {
        query_id: string;
        query: string;
        filters?: ShopSearchFilters;
        result_count: number;
        latency_ms?: number;
        impressions?: Array<{
            object_type: string;
            object_id: number | string;
            variant_id?: number | null;
            rank: number;
        }>;
        session_token?: string | null;
    },
): Promise<{ accepted?: boolean; session_token?: string | null } | null> {
    try {
        return await postJson(init, '/search/telemetry/run', {
            query_id: payload.query_id,
            surface: init.surface || 'shop.catalog',
            experience: init.experience_key || 'shop.catalog',
            locale: init.locale || 'fr',
            query: payload.query,
            filters: payload.filters || null,
            result_count: payload.result_count,
            latency_ms: payload.latency_ms ?? null,
            path: 'deterministic',
            session_token: payload.session_token || null,
            impressions: payload.impressions || [],
        });
    } catch {
        return null;
    }
}

export async function trackSearchEvent(
    init: ShopSearchCatalogInitData,
    payload: {
        query_id: string;
        event_type: 'click' | 'add_to_cart' | 'filter_change';
        object_type?: string;
        object_id?: number | string;
        variant_id?: number | null;
        rank?: number | null;
        session_token?: string | null;
    },
): Promise<void> {
    try {
        await postJson(init, '/search/telemetry/event', {
            query_id: payload.query_id,
            event_type: payload.event_type,
            object_type: payload.object_type || 'shop_product',
            object_id: payload.object_id ?? null,
            variant_id: payload.variant_id ?? null,
            rank: payload.rank ?? null,
            session_token: payload.session_token || null,
        });
    } catch {
        // Consentement / mode off : silencieux.
    }
}
